/**
 * RecommendationsCard · sugerencias personalizadas (rule-based backend).
 *
 * Sprint Hito 6 · Fase 7 · 2026-05-30 · Carlos Beltrán
 *
 * Renderiza listado priorizado:
 *   1. Lesson en curso (Continúa donde lo dejaste)
 *   2. Quiz pendiente
 *   3. Evento en vivo próximo esta semana
 *   4. Recompensa siguiente (XP faltante)
 *
 * Vive en Dashboard como sección sticky · click CTA → URL relativa.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { authBearer } from '../lib/impersonation';

interface Recommendation {
  kind:
    | 'lesson_in_progress'
    | 'quiz_pending'
    | 'upcoming_event'
    | 'next_reward';
  priority: number;
  title: string;
  subtitle: string;
  cta_label: string;
  cta_url: string;
  metadata?: Record<string, unknown>;
}

interface ResponseShape {
  recommendations: Recommendation[];
  total: number;
}

const KIND_ICON: Record<Recommendation['kind'], string> = {
  lesson_in_progress: '▶️',
  quiz_pending: '🧠',
  upcoming_event: '🎬',
  next_reward: '🏅',
};

export default function RecommendationsCard(): JSX.Element | null {
  const { state } = useAuth();
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Bearer efectivo: token de impersonación si lo hay (prioridad), si no el JWT
  // real. Sin esto, en vista-alumno se mandaba el JWT del admin (leak).
  const jwt: string | null =
    state.status === 'authenticated'
      ? authBearer(state.identity)
      : null;

  const load = useCallback(async () => {
    if (!jwt) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/me/recommendations', {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as ResponseShape;
      setRecs(data.recommendations);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }, [jwt]);

  useEffect(() => { void load(); }, [load]);

  if (state.status !== 'authenticated' || !state.identity) return null;
  if (loading && recs.length === 0) {
    return (
      <section className="card p-5" aria-live="polite">
        <p className="text-sm text-ink/55">Cargando recomendaciones…</p>
      </section>
    );
  }
  if (error || recs.length === 0) return null;

  return (
    <section
      className="card p-5"
      aria-labelledby="recommendations-heading"
      data-testid="recommendations-card"
    >
      <header className="mb-3">
        <h2 id="recommendations-heading" className="text-base font-semibold text-ink">
          🎯 Tu próximo paso
        </h2>
        <p className="mt-0.5 text-xs text-ink/55">
          Recomendado para ti según tu progreso reciente
        </p>
      </header>

      <ul className="space-y-2">
        {recs.map((r) => (
          <li
            key={r.kind}
            data-testid={`recommendation-${r.kind}`}
            className="rounded-md border border-amber-400/20 bg-amber-500/5 p-3"
          >
            {r.kind === 'upcoming_event' &&
              typeof r.metadata?.thumbnail_url === 'string' && (
                <Link
                  to="/roboteam"
                  className="mb-2 block"
                  aria-label={r.title}
                >
                  <img
                    src={r.metadata.thumbnail_url}
                    alt=""
                    className="aspect-[4/1] w-full rounded object-cover"
                    loading="lazy"
                  />
                </Link>
              )}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink">
                  <span className="mr-2" aria-hidden="true">{KIND_ICON[r.kind]}</span>
                  {r.title}
                </p>
                <p className="mt-0.5 text-xs text-ink/55">{r.subtitle}</p>
              </div>
              <Link
                to={r.cta_url}
                className="shrink-0 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-medium text-amber-950 hover:bg-amber-400"
                data-testid={`recommendation-cta-${r.kind}`}
              >
                {r.cta_label}
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
